import { useState, useEffect, useRef, Suspense } from 'react'
import { NavLink, useLocation, useNavigate, Outlet } from 'react-router-dom'

function isCexTerminalPath(pathname) {
  return /\/project\/group\/[^/]+\/terminal\//.test(pathname || '')
}
import { RouteErrorBoundary } from './RouteErrorBoundary.jsx'
import { prefetchRouteForPath } from '../lib/prefetchRoutes.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import { useGroups } from '../contexts/GroupsContext.jsx'
import { useTheme } from '../contexts/ThemeContext.jsx'
import { usePlatform } from '../contexts/PlatformContext.jsx'
import { SquaresFour } from '@phosphor-icons/react/SquaresFour'
import { FolderOpen } from '@phosphor-icons/react/FolderOpen'
import { Database } from '@phosphor-icons/react/Database'
import { FileText } from '@phosphor-icons/react/FileText'
import { Fire } from '@phosphor-icons/react/Fire'
import { ArrowsLeftRight } from '@phosphor-icons/react/ArrowsLeftRight'
import { LOTOS_LOGO_DARK, LOTOS_LOGO_LIGHT } from '../lib/branding.js'
import { CaretLeft } from '@phosphor-icons/react/CaretLeft'
import { User } from '@phosphor-icons/react/User'
import { SignOut } from '@phosphor-icons/react/SignOut'
import { Moon } from '@phosphor-icons/react/Moon'
import { Sun } from '@phosphor-icons/react/Sun'
import { AccountPnlPanel } from '../pages/cex/terminal/AccountPnlPanel.jsx'

const SIDEBAR_COLLAPSED_KEY = 'lotos_dashboard_sidebar_collapsed'

function getStoredSidebarCollapsed() {
  try {
    const v = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    return v === '1' || v === 'true'
  } catch {
    return false
  }
}
function setStoredSidebarCollapsed(collapsed) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0')
  } catch (_) {}
}

const DEX_HOST_ORIGIN = 'https://dex.lotosterminal.pro'
const CEX_HOST_ORIGIN = 'https://cex.lotosterminal.pro'

function pathWithQueryAndHash() {
  if (typeof window === 'undefined') return '/'
  const p = window.location.pathname || '/'
  return `${p}${window.location.search || ''}${window.location.hash || ''}`
}

const baseNavItems = [
  { to: '/', label: 'Dashboard', icon: SquaresFour },
  { to: '/project', label: 'Project', icon: FolderOpen },
  { to: '/knowledge', label: 'Knowledge Base', icon: Database },
]

/**
 * На `*.lotosterminal.pro` — переход на другой поддомен **с тем же path/query/hash**
 * (обновление страницы / закладка остаётся на том же маршруте). Локально: только `activePlatform` + `navigate`.
 */
function applyPlatformToHost(platform, { canAccessDex, canAccessCex, isCeo, setActivePlatform, navigate }) {
  const useDex = isCeo || canAccessDex
  const useCex = isCeo || canAccessCex
  if (platform !== 'dex' && platform !== 'cex') return
  if (typeof window === 'undefined') return
  if (platform === 'dex' && !useDex) return
  if (platform === 'cex' && !useCex) return
  const host = window.location.hostname
  if (!host.endsWith('lotosterminal.pro')) {
    setActivePlatform(platform)
    navigate(pathWithQueryAndHash(), { replace: true })
    return
  }
  if (platform === 'dex' && host.startsWith('dex.')) {
    setActivePlatform('dex')
    return
  }
  if (platform === 'cex' && host.startsWith('cex.')) {
    setActivePlatform('cex')
    return
  }
  const base = platform === 'dex' ? DEX_HOST_ORIGIN : CEX_HOST_ORIGIN
  window.location.assign(`${base}${pathWithQueryAndHash()}`)
}

export default function Layout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => getStoredSidebarCollapsed())
  const [contentVisible, setContentVisible] = useState(() => !getStoredSidebarCollapsed())
  const {
    user,
    logout,
    isAdmin,
    isCeo,
    isClient,
    canViewLogs,
    canAccessWarmup,
    canAccessDex,
    canAccessCex,
  } = useAuth()
  const location = useLocation()
  const scrollContainerRef = useRef(null)

  // Сохраняем позицию скролла при уходе с маршрута, восстанавливаем при возврате
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return undefined
    const path = location.pathname
    try {
      const saved = sessionStorage.getItem('scroll:' + path)
      const pos = saved !== null ? parseInt(saved, 10) : 0
      if (!isNaN(pos)) {
        // rAF гарантирует что контент уже отрисован
        const id = requestAnimationFrame(() => { container.scrollTop = pos })
        return () => {
          cancelAnimationFrame(id)
          try { sessionStorage.setItem('scroll:' + path, String(container.scrollTop)) } catch (_) {}
        }
      }
    } catch (_) {}
    return () => {
      try { sessionStorage.setItem('scroll:' + path, String(container?.scrollTop ?? 0)) } catch (_) {}
    }
  }, [location.pathname])

  /** Один UI: переключатель DEX/CEX у всех, у кого есть сессия. Доступ к сторонам — через disabled на кнопках. */
  const showPlatformToggle = Boolean(user)
  const canKb = user?.canAccessKnowledgeCatalog === true
  const { activePlatform, setActivePlatform, health } = usePlatform()
  const canUseDexPlatform = isCeo || canAccessDex
  const canUseCexPlatform = isCeo || canAccessCex

  useEffect(() => {
    setContentVisible(!sidebarCollapsed)
  }, [sidebarCollapsed])

  /**
   * Smart subdomain redirect: if the user landed on a subdomain they have no
   * access to, send them to the one they can use. Keeps the current pathname
   * (session cookie on `.lotosterminal.pro` survives the jump).
   */
  useEffect(() => {
    if (!user) return
    if (typeof window === 'undefined') return
    const host = window.location.hostname
    if (!host.endsWith('lotosterminal.pro')) return
    const onCex = host.startsWith('cex.')
    const onDex = host.startsWith('dex.')
    if (!onCex && !onDex) return
    const jump = pathWithQueryAndHash()
    if (onCex && !canAccessCex && canAccessDex) {
      window.location.assign(`https://dex.lotosterminal.pro${jump}`)
    } else if (onDex && !canAccessDex && canAccessCex) {
      window.location.assign(`https://cex.lotosterminal.pro${jump}`)
    }
  }, [user, canAccessDex, canAccessCex])

  /** Clamp activePlatform to what the user can actually see. */
  useEffect(() => {
    if (!user) return
    if (activePlatform === 'cex' && !canAccessCex && canAccessDex) {
      setActivePlatform('dex')
    } else if (activePlatform === 'dex' && !canAccessDex && canAccessCex) {
      setActivePlatform('cex')
    }
  }, [user, activePlatform, canAccessDex, canAccessCex, setActivePlatform])

  /** CEX: без Warmup / Distributor в меню (только DEX). Остальное совпадает. */
  const showWarmupNav = canAccessWarmup && activePlatform !== 'cex'
  const navItems = isClient
    ? [baseNavItems[0], ...(canKb ? [baseNavItems[2]] : [])]
    : [
        baseNavItems[0],
        baseNavItems[1],
        ...(canKb ? [baseNavItems[2]] : []),
        ...(canViewLogs ? [{ to: '/logs', label: 'Logs', icon: FileText }] : []),
        ...(showWarmupNav
          ? [
              { to: '/warmup', label: 'Warmup', icon: Fire },
              { to: '/warmup/exchange-distributor', label: 'Distributor', icon: ArrowsLeftRight },
            ]
          : []),
      ]

  const { syncError, saving, flushBeforeUnload } = useGroups()
  const { isDark, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const cexTerminalFill = isCexTerminalPath(location.pathname)
  const terminalAccountId = cexTerminalFill ? new URLSearchParams(location.search).get('account') || null : null
  const terminalSymbol = (() => {
    if (!cexTerminalFill) return null
    const m = (location.pathname || '').match(/\/project\/group\/[^/]+\/terminal\/[^/]+\/([^/?#]+)/)
    if (!m?.[1]) return null
    try {
      return decodeURIComponent(m[1])
    } catch {
      return m[1]
    }
  })()
  const canViewTerminalPnl = Boolean(user?.canViewPnl === true || isCeo || isAdmin)
  const logoSrc = isDark ? LOTOS_LOGO_DARK : LOTOS_LOGO_LIGHT

  const handleCollapse = () => {
    setSidebarCollapsed(true)
    setStoredSidebarCollapsed(true)
  }
  const handleExpand = () => {
    setSidebarCollapsed(false)
    setStoredSidebarCollapsed(false)
  }
  const handleLogout = () => {
    flushBeforeUnload?.()
    logout()
    navigate('/login')
  }

  const isCex = activePlatform === 'cex'
  /** Только подтверждённо упавший health === false, не «ещё пингуем» (null) */
  const targetOffline = isCex ? health.cex === false : health.dex === false
  const targetLabel = isCex ? 'CEX' : 'DEX'

  return (
    <div
      className={`flex w-full min-h-0 ${cexTerminalFill ? 'h-dvh max-h-dvh overflow-hidden' : 'min-h-screen'}`}
    >
      {syncError && (
        <div className="fixed left-0 right-0 top-0 z-50 bg-amber-500/90 px-4 py-1.5 text-center text-sm text-slate-900">
          {syncError}
        </div>
      )}
      {saving && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border border-slate-300/80 bg-white/95 px-3 py-1.5 text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-800/95 dark:text-slate-300">
          Saving…
        </div>
      )}
      <aside
        className="fixed left-0 top-0 z-40 grid h-dvh min-h-0 w-[var(--sidebar-width)] max-h-dvh grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden sidebar-glass border-r border-white/30"
        style={{ '--sidebar-width': sidebarCollapsed ? '72px' : '14rem' }}
      >
        <div
          className={`flex h-14 shrink-0 items-center px-3 ${
            sidebarCollapsed ? 'justify-center' : 'gap-3'
          }`}
        >
          <NavLink
            to="/"
            onClick={(e) => {
              if (sidebarCollapsed) {
                e.preventDefault()
                handleExpand()
              }
            }}
            className="flex shrink-0 items-center rounded-lg p-0.5 hover:bg-white/20 bg-transparent"
            title="Dashboard"
          >
            <img
              key={isDark ? 'dark-theme' : 'light-theme'}
              src={logoSrc}
              alt="Lotos"
              className="h-10 w-10 object-contain"
            />
          </NavLink>
          {contentVisible && (
            <>
              <NavLink
                to="/"
                onClick={sidebarCollapsed ? handleExpand : undefined}
                className="min-w-0 flex-1 truncate text-center text-base font-semibold leading-none text-slate-700 rounded-lg py-0.5 hover:bg-white/20 hover:text-slate-800"
                title="Dashboard"
              >
                terminal.exe
              </NavLink>
              <button
                type="button"
                onClick={handleCollapse}
                className="flex shrink-0 items-center justify-center rounded-lg p-1.5 text-slate-500 hover:bg-white/20 hover:text-slate-700"
                title="Collapse"
              >
                <CaretLeft className="h-5 w-5" weight="bold" />
              </button>
            </>
          )}
        </div>

        <nav className="min-h-0 space-y-0.5 overflow-y-auto overflow-x-hidden overscroll-contain p-2 [-webkit-overflow-scrolling:touch]">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/' || to === '/warmup'}
              onPointerEnter={() => prefetchRouteForPath(to)}
              onFocus={() => prefetchRouteForPath(to)}
              onTouchStart={() => prefetchRouteForPath(to)}
              onClick={sidebarCollapsed ? handleExpand : undefined}
              title={sidebarCollapsed ? label : undefined}
              className={({ isActive }) =>
                `flex w-full items-center gap-3 rounded-lg py-2.5 px-3 text-sm font-medium outline-none
                ${isActive
                  ? 'text-slate-900 dark:text-slate-100'
                  : 'text-slate-600 hover:bg-white/25 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-200'
                }
                ${sidebarCollapsed ? 'justify-center px-0' : ''}`
              }
            >
              <Icon className="h-5 w-5 shrink-0" />
              {contentVisible && <span className="sidebar-link-text">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Grid row 3: keep Platform + Account in one auto-sized row (Safari clips flex+overflow-hidden otherwise). */}
        <div className="flex min-h-0 flex-col overflow-x-hidden">
        {contentVisible && cexTerminalFill && terminalAccountId && terminalSymbol && canViewTerminalPnl ? (
          <div className="px-2 pb-2">
            <AccountPnlPanel
              accountId={terminalAccountId}
              symbol={terminalSymbol}
              canViewPnl={canViewTerminalPnl}
            />
          </div>
        ) : null}
        {showPlatformToggle && (
          <div className="relative z-20 shrink-0 px-2 pb-2 pt-2">
            {contentVisible ? (
              <div>
                <div className="mb-1.5 flex items-center justify-between px-1.5">
                  <p className="text-[11px] font-normal normal-case leading-tight text-slate-500 dark:text-slate-400">
                    Platform
                  </p>
                  {targetOffline && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                      {targetLabel} offline
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-1 rounded-xl border border-slate-200/70 bg-slate-100/90 p-1 shadow-sm dark:border-white/5 dark:bg-transparent dark:shadow-none">
                  <button
                    type="button"
                    onClick={() =>
                      applyPlatformToHost('dex', {
                        canAccessDex,
                        canAccessCex,
                        isCeo,
                        setActivePlatform,
                        navigate,
                      })
                    }
                    disabled={!canUseDexPlatform}
                    className={`rounded-lg px-2 py-1.5 text-xs font-bold uppercase tracking-wide transition
                    ${!canUseDexPlatform
                        ? 'cursor-not-allowed opacity-40'
                        : !isCex
                          ? 'bg-white text-slate-900 shadow dark:bg-white/20 dark:text-slate-100'
                          : 'text-slate-500 hover:bg-white/50 dark:text-slate-400 dark:hover:bg-white/10'
                      }`}
                    title={canUseDexPlatform ? 'Open DEX (dex.…)' : 'No DEX access'}
                  >
                    DEX
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      applyPlatformToHost('cex', {
                        canAccessDex,
                        canAccessCex,
                        isCeo,
                        setActivePlatform,
                        navigate,
                      })
                    }
                    disabled={!canUseCexPlatform}
                    className={`rounded-lg px-2 py-1.5 text-xs font-bold uppercase tracking-wide transition
                    ${!canUseCexPlatform
                        ? 'cursor-not-allowed opacity-40'
                        : isCex
                          ? 'bg-white text-slate-900 shadow dark:bg-white/20 dark:text-slate-100'
                          : 'text-slate-500 hover:bg-white/50 dark:text-slate-400 dark:hover:bg-white/10'
                      }`}
                    title={canUseCexPlatform ? 'Open CEX (cex.…)' : 'No CEX access'}
                  >
                    CEX
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() =>
                  applyPlatformToHost(isCex ? 'dex' : 'cex', {
                    canAccessDex,
                    canAccessCex,
                    isCeo,
                    setActivePlatform,
                    navigate,
                  })
                }
                disabled={isCex ? !canUseDexPlatform : !canUseCexPlatform}
                className="mx-auto flex h-8 w-full items-center justify-center rounded-lg bg-slate-100/80 text-[10px] font-bold uppercase tracking-wide text-slate-700 ring-1 ring-slate-200/80 hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-transparent dark:text-slate-200 dark:ring-white/10"
                title={isCex ? 'CEX — click for DEX' : 'DEX — click for CEX'}
              >
                {isCex ? 'CEX' : 'DEX'}
              </button>
            )}
          </div>
        )}

        {/* Account block */}
        <div className="account-block shrink-0 p-2">
          {sidebarCollapsed && (
            <div className="mb-2 flex justify-center">
              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center justify-center rounded-lg p-2 text-slate-500 hover:bg-white/25 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-200"
                title={isDark ? 'Light theme' : 'Dark theme'}
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
            </div>
          )}
          <p
            className={`mb-1.5 min-w-0 truncate text-[11px] font-normal normal-case leading-tight text-slate-500 dark:text-slate-400 ${
              sidebarCollapsed ? 'text-center' : 'px-1.5'
            }`}
          >
            Account
          </p>
          <div
            className={`flex items-center gap-2 px-0 py-0.5 ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}
          >
            <NavLink
              to="/settings"
              onPointerEnter={() => prefetchRouteForPath('/settings')}
              onFocus={() => prefetchRouteForPath('/settings')}
              onTouchStart={() => prefetchRouteForPath('/settings')}
              onClick={sidebarCollapsed ? handleExpand : undefined}
              className={({ isActive }) =>
                `flex min-w-0 flex-1 items-center gap-2 rounded-lg py-1.5 pr-1 text-sm
                ${isActive ? 'text-slate-900 dark:text-slate-100' : 'hover:bg-white/25 dark:hover:bg-white/8'}
                ${sidebarCollapsed ? 'flex-1 justify-center' : ''}`
              }
            >
              <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full bg-white/60 ring-2 ring-slate-200/80 dark:bg-white/10 dark:ring-white/20">
                {user?.avatar ? (
                  <img src={user.avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-600 dark:text-slate-400">
                    <User className="h-4 w-4" />
                  </div>
                )}
              </div>
              {contentVisible && (
                <span className="sidebar-link-text min-w-0 truncate font-medium text-slate-700 dark:text-slate-200">
                  {user?.login ?? 'Account'}
                </span>
              )}
            </NavLink>
            {contentVisible && (
              <>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="flex shrink-0 items-center justify-center rounded-lg p-2 text-slate-500 hover:bg-white/25 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-200"
                  title={isDark ? 'Light theme' : 'Dark theme'}
                >
                  {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex shrink-0 items-center justify-center rounded-lg p-2 text-slate-500 hover:bg-white/25 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-slate-200"
                  title="Log out"
                >
                  <SignOut className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>
        </div>
      </aside>

      <main
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        style={{
          marginLeft: sidebarCollapsed ? 72 : 224,
          paddingTop: syncError ? '2.5rem' : undefined,
        }}
      >
        <div
          className={
            cexTerminalFill
              ? 'flex h-0 min-h-0 flex-1 flex-col overflow-hidden p-4 md:p-5'
              : 'min-h-screen overflow-y-auto p-6 md:p-8'
          }
          ref={cexTerminalFill ? undefined : scrollContainerRef}
        >
          <RouteErrorBoundary>
            <Suspense
              fallback={
                <div className="flex min-h-[40vh] items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                  Loading…
                </div>
              }
            >
              {cexTerminalFill ? (
                <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
                  <Outlet />
                </div>
              ) : (
                <Outlet />
              )}
            </Suspense>
          </RouteErrorBoundary>
        </div>
      </main>
    </div>
  )
}
